import "./app.css";
/* Component aliases AFTER semantic (inside app.css) and BEFORE density, so
   --input-* exist and density remaps flow through them. */
import "./component.css";
/* Density AFTER app.css/@theme so --radius-* and control tokens remap by mode. */
import "./density.css";
import "./styles/cell-icon-button.css";
import "./focus.css";
import "./link.css";

import { TooltipProvider } from "@centric/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NuqsAdapter } from "nuqs/adapters/react-router/v8";
import { type ReactNode, StrictMode, useState } from "react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";

import { Toaster } from "~/components/ui/sonner";
import { AuthProvider } from "~/features/auth";
import { I18nProvider } from "~/features/i18n";
// Deep import, deliberately. `~/features/imageViewer` re-exports the whole
// feature, so importing the provider through the barrel pulls the viewer's
// siblings — and through them `businessObjects` and `documents` — into the
// entry bundle on every page load. This module is provider-only.
import { ImageViewerProvider } from "~/features/imageViewer/context/ImageViewerProvider";
import { UserPreferencesProvider } from "~/features/userPreferences";
// Deep import for the same reason as ImageViewerProvider above: the
// `workflowRun` barrel exports the run/definition pages and their schema
// tooling, and pulling those into the entry bundle to reach one provider would
// cost every page load.
import { NotificationSocketProvider } from "~/features/workflowRun/notifications/NotificationSocketProvider";
import { WorkflowRunTrackerProvider } from "~/features/workflowRun/runTracker/WorkflowRunTrackerProvider";
import { UnsavedChangesProvider } from "~/hooks/unsavedChanges";
import { QueryCacheLifecycle } from "~/lib/QueryCacheLifecycle";
import { queryDefaults } from "~/lib/queryClient";

import type { Route } from "./+types/root";

export const links: Route.LinksFunction = () => [
  { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
];

export const Layout = ({ children }: { children: ReactNode }) => (
  <html lang="en" suppressHydrationWarning data-density="normal">
    <head>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {/* Production CSP. `script-src` deliberately keeps 'unsafe-inline' and carries NO hash:
            React Router injects inline hydration scripts we cannot hash, and browsers ignore
            'unsafe-inline' as soon as a hash is present — adding one would block them.
            Tightening this needs a per-request nonce on a real response header, not this meta tag.
            Not applied in dev: the dev-only scripts below load from unpkg, which 'self' would block. */}
      {!import.meta.env.DEV && (
        <meta
          httpEquiv="Content-Security-Policy"
          content={[
            "default-src 'self'",
            "script-src 'self' 'unsafe-inline'",
            "style-src 'self' 'unsafe-inline'",
            "font-src 'self'",
            "img-src 'self' data: blob:",
            "connect-src 'self' https://*.centricsoftware.io wss://*.centricsoftware.io",
            "frame-src 'none'",
            "object-src 'none'",
            "base-uri 'self'",
            "form-action 'self'",
          ].join("; ")}
        />
      )}
      <Meta />
      <Links />
      <title>Centric UI</title>

      {/* Prevent dark mode FOUC — apply .dark class before React hydrates.
            Permitted by 'unsafe-inline' in the CSP above; there is no hash to keep in sync. */}
      {/* react-doctor-disable-next-line react-doctor/no-danger -- controlled theme-init markup, no user content (FOUC prevention) */}
      <script
        dangerouslySetInnerHTML={{
          __html: `(function(){try{var p=JSON.parse(localStorage.getItem("user-preferences")||"{}");var t=p.theme||"light";var d=t==="dark"||(t==="system"&&matchMedia("(prefers-color-scheme:dark)").matches);if(d)document.documentElement.classList.add("dark");var dens=p.density||"normal";if(dens==="compact"||dens==="normal"||dens==="spacious")document.documentElement.setAttribute("data-density",dens);else document.documentElement.setAttribute("data-density","normal")}catch(e){try{document.documentElement.setAttribute("data-density","normal")}catch(_){}}})()`,
        }}
      />
      {import.meta.env.DEV && import.meta.env.VITE_REACT_GRAB && (
        <script
          src="https://unpkg.com/react-grab@0.1.24/dist/index.global.js"
          integrity="sha384-GiJwOyBWULpormT43b6ga5fvKmnxVJLjUOoBEgCoVXZQG5OWEpsRYPepWqW8eYc4"
          crossOrigin="anonymous"
          async
        />
      )}
      {import.meta.env.DEV && import.meta.env.VITE_REACT_SCAN && (
        <script
          src="https://unpkg.com/react-scan@0.5.3/dist/auto.global.js"
          integrity="sha384-NKPlMoHDbpRLyDckGBIEFYoOAulT82BVq/h/KZUo9Zd0v4KrsDdOOkSqiMdlQwrw"
          crossOrigin="anonymous"
          async
        />
      )}
    </head>
    <body suppressHydrationWarning>
      {children}
      <ScrollRestoration />
      <Scripts />
    </body>
  </html>
);

export default function App() {
  const [queryClient] = useState(() => new QueryClient({ defaultOptions: queryDefaults }));

  return (
    <StrictMode>
      <NuqsAdapter>
        <UserPreferencesProvider>
          <I18nProvider>
            <AuthProvider>
              <QueryClientProvider client={queryClient}>
                <QueryCacheLifecycle />
                <TooltipProvider>
                  <ImageViewerProvider>
                    <UnsavedChangesProvider>
                      {/* Inside QueryClientProvider (it invalidates collections)
                          and inside the router (its toast links to the run). */}
                      <NotificationSocketProvider>
                        <WorkflowRunTrackerProvider>
                          <Outlet />
                        </WorkflowRunTrackerProvider>
                      </NotificationSocketProvider>
                    </UnsavedChangesProvider>
                  </ImageViewerProvider>
                </TooltipProvider>
                {/* Neutral shadcn-styled toasts (popover bg + status icons) —
                    no richColors: success is no longer the green bar. */}
                <Toaster position="top-right" />
              </QueryClientProvider>
            </AuthProvider>
          </I18nProvider>
        </UserPreferencesProvider>
      </NuqsAdapter>
    </StrictMode>
  );
}

export const ErrorBoundary = ({ error }: Route.ErrorBoundaryProps) => {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404 ? "The requested page could not be found." : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
};
